import { useLocalStorage } from "@/composables/useLocalStorage";
import { useUserStore } from "@/store/userStore";
import axios, { HttpStatusCode } from "axios";
import { clearAppSession } from "@/utils/appSession";

// Sesión única (docs/single-session-fe-contract.md §3): la API cerró ESTA
// sesión. REPLACED = se entró desde otro equipo (se avisa en el login);
// ENDED = logout en otra pestaña (al login sin aviso). No se llama a
// /auth/logout: la sesión ya no existe.
const SESSION_CLOSED_REDIRECT = {
  SESSION_REPLACED: "/login?reason=replaced",
  SESSION_ENDED: "/login",
};
import { hasDemoSession } from "@/composables/useDemoAccess";

const instance = axios.create({
  baseURL: process.env.VUE_APP_API,
  headers: {
    "Content-Type": "application/json;charset=UTF-8",
  },
});

const setup = (instance, contentType = "application/json;charset=UTF-8") => {

  const { getLocalAccessToken, getSelectedCompanyId } = useLocalStorage();

  instance.interceptors.request.use(
    (config) => {
      const token = getLocalAccessToken();
      if (token) {
        config.headers["x-token"] = token;
      }
      const selectedCompanyId = getSelectedCompanyId();
      if (selectedCompanyId && !config.skipCompanyId) {
        config.headers["x-company-id"] = selectedCompanyId;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (res) => {
      return res;
    },
    async (err) => {
        if (err.response?.status === HttpStatusCode.Unauthorized) {
          // Public data calls (x-secret, e.g. the public company landing /
          // profile) must NOT log the user out on a 401 — the page handles the
          // missing data gracefully. Only bounce an AUTHENTICATED session whose
          // token expired (there is a token AND the call was not public).
          const isPublicCall = !!err.config?.headers?.['x-secret'];
          const hasToken = !!getLocalAccessToken();
          if (hasToken && !isPublicCall && hasDemoSession()) {
            // Demo cortada o vencida (o el token del perfil): sale la sesión del
            // perfil y vuelve al selector; si la demo tampoco sirve, el selector
            // pide las credenciales de staff. Ver docs/BE-RESPONSE-DEMO-ACCESS.md.
            clearAppSession();
            window.location = "/login?demo=1&pick=1";
          } else if (hasToken && !isPublicCall && SESSION_CLOSED_REDIRECT[err.response.data?.code]) {
            clearAppSession();
            window.location = SESSION_CLOSED_REDIRECT[err.response.data.code];
          } else if (hasToken && !isPublicCall) {
            localStorage.clear();
            // Also drop the action plan selection persisted in sessionStorage
            // so the next sign-in doesn't reuse the previous user's planId.
            sessionStorage.removeItem('gme.actionPlan.currentPlanId');
            window.location = "/";
          }
        }
        // Solo sincroniza el estado; no reabre el modal (eso lo hacen
        // useBlockWhileLapsed, isSubscriptionLapsed y el timer de App.vue).
        if (err.response?.status === HttpStatusCode.Forbidden
          && err.response?.data?.code === 'PLAN_EXPIRED') {
          const subscription = err.response.data.subscription;
          if (subscription) useUserStore().updateSubscription(subscription);
        }
      return err.response
    }
  );

  instance.defaults.headers["Content-Type"] = contentType;

};

setup(instance, "application/json");

export default instance;
